/**
 * The L0 KPI strip at the top of the Defender board (§1): at most six numbers
 * that answer "is anything wrong right now?", each tile a door down to the
 * section that explains it.
 *
 * **A tile only exists when its panel actually read something.** The four data
 * panels fail independently, and a tile for a panel that answered 403 would be
 * an em dash sitting in a row of real figures — which reads as "we looked and
 * found nothing" rather than "we were not allowed to look". So a panel that is
 * not `ok` contributes no tile at all, and its access panel below carries the
 * explanation instead. Today that means the strip is one tile wide: the consent
 * position, which is the only Defender number NOVA can currently measure.
 *
 * That consent tile is deliberately first and always present. `/defender/permissions`
 * makes no Graph call, so it answers in every state, and "how much of this board
 * is switched on" is the single most useful figure on the page while the board is
 * dark.
 *
 * Two honesty rules do real work here:
 *
 * - **`null` is neutral, never green and never zero.** Every counter on this
 *   board is `number | null` where `0` is a measured zero and `null` is a read
 *   that never answered. Zero open incidents is genuinely good news; "we could
 *   not ask about incidents" is not, and the tone is the only thing telling them
 *   apart at a glance. No null takes a `success` tone anywhere in this file.
 * - **`oldest_open_days` is null for two different reasons**, and the tile says
 *   which. Null means either nothing is open — the good case — or the read
 *   failed. `open_incidents` is what tells them apart, so the caption is derived
 *   from both fields rather than from the age alone.
 */

import { Card, Statistic } from "@norican/react";
import type { ReactNode } from "react";

import type {
  DefenderAlerts,
  DefenderIdentityHealth,
  DefenderIncidents,
  DefenderPermissions,
  DefenderSecureScore,
} from "@/lib/api";
import type { StatusTone } from "@/lib/charts/config";

import {
  ALERTS_ANCHOR,
  IDENTITY_ANCHOR,
  INCIDENTS_ANCHOR,
  SECURE_SCORE_ANCHOR,
  SETUP_ANCHOR,
} from "./params";
import {
  cautionTone,
  EM_DASH,
  formatCount,
  formatDays,
  formatPercent,
  formatPoints,
  formatPointsDelta,
  HEALTH_LABEL,
  HEALTH_TONE,
  isPanelOk,
  oldestOpenTone,
  problemTone,
  STALE_INCIDENT_DAYS,
  volumeTone,
} from "./status";

interface KpiTileProps {
  /** In-page anchor of the section that explains this number. */
  href: string;
  tone: StatusTone | "accent";
  children: ReactNode;
}

/** One KPI tile: a card that is also a link to its section (§4.1 — no dead aggregates). */
function KpiTile({ href, tone, children }: KpiTileProps) {
  return (
    <a href={href} className="block rounded focus-visible:outline-2">
      <Card
        variant="outline"
        accent="left"
        accentTone={tone === "accent" ? "accent" : tone}
        interactive
      >
        <Card.Body>{children}</Card.Body>
      </Card>
    </a>
  );
}

interface DefenderKpiStripProps {
  permissions: DefenderPermissions;
  incidents?: DefenderIncidents;
  alerts?: DefenderAlerts;
  secureScore?: DefenderSecureScore;
  identity?: DefenderIdentityHealth;
}

export function DefenderKpiStrip({
  permissions,
  incidents,
  alerts,
  secureScore,
  identity,
}: DefenderKpiStripProps) {
  const requiredCount = permissions.required.length;
  const consentTone: StatusTone =
    permissions.granted_count == null
      ? "neutral"
      : permissions.granted_count === requiredCount
        ? "success"
        : "warning";

  const incidentsLive = incidents != null && isPanelOk(incidents);
  const alertsLive = alerts != null && isPanelOk(alerts);
  const scoreLive = secureScore != null && isPanelOk(secureScore);
  const identityLive = identity != null && isPanelOk(identity);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {/* Always present: the one Defender figure that needs no Graph call. */}
      <KpiTile href={`#${SETUP_ANCHOR}`} tone={consentTone}>
        <Statistic
          label="Graph permissions granted"
          value={
            permissions.granted_count == null
              ? `${EM_DASH} / ${formatCount(requiredCount)}`
              : `${formatCount(permissions.granted_count)} / ${formatCount(requiredCount)}`
          }
          tone={consentTone}
          size="lg"
          caption={
            permissions.granted_count == null
              ? "None has been exercised yet — unknown, not zero granted and not zero missing"
              : permissions.granted_count === requiredCount
                ? "Every panel on this board can read"
                : "The panels below that cannot read say which permission they need"
          }
        />
      </KpiTile>

      {incidentsLive && (
        <KpiTile href={`#${INCIDENTS_ANCHOR}`} tone={problemTone(incidents.open_incidents)}>
          <Statistic
            label="Open incidents"
            value={formatCount(incidents.open_incidents)}
            tone={problemTone(incidents.open_incidents)}
            size="lg"
            caption={
              incidents.open_incidents == null
                ? "The incident read failed — unknown, not zero"
                : incidents.truncated
                  ? "At least this many — paging stopped at NOVA's cap, so this is a floor"
                  : `Unresolved of ${formatCount(incidents.incidents_total)} created in the window`
            }
          />
        </KpiTile>
      )}

      {incidentsLive && (
        <KpiTile
          href={`#${INCIDENTS_ANCHOR}`}
          tone={oldestOpenTone(incidents.open_incidents, incidents.oldest_open_days)}
        >
          <Statistic
            label="Oldest open incident"
            value={formatDays(incidents.oldest_open_days)}
            tone={oldestOpenTone(incidents.open_incidents, incidents.oldest_open_days)}
            size="lg"
            // Null here has two causes and they are opposites, so the caption is
            // derived from `open_incidents` as well as from the age itself.
            caption={
              incidents.open_incidents == null
                ? "The incident read failed, so no age was computed — unknown, not young"
                : incidents.open_incidents === 0
                  ? "Nothing is open, so there is no oldest. A measured absence, not a failed read."
                  : incidents.oldest_open_days == null
                    ? "Open incidents carried no usable creation timestamp, so their age is unknown"
                    : `Warns from ${STALE_INCIDENT_DAYS} days — past that it is a finding, not a queue`
            }
          />
        </KpiTile>
      )}

      {alertsLive && (
        <KpiTile href={`#${ALERTS_ANCHOR}`} tone={problemTone(alerts.open_alerts)}>
          <Statistic
            label="Open alerts"
            value={formatCount(alerts.open_alerts)}
            tone={problemTone(alerts.open_alerts)}
            size="lg"
            caption={
              alerts.open_alerts == null
                ? "The alert read failed — unknown, not zero"
                : `Unresolved of ${formatCount(alerts.alerts_total)} raised in the window${
                    alerts.truncated ? " (a floor — paging hit NOVA's cap)" : ""
                  }`
            }
          />
        </KpiTile>
      )}

      {scoreLive && (
        <KpiTile href={`#${SECURE_SCORE_ANCHOR}`} tone={volumeTone(secureScore.score_pct)}>
          <Statistic
            label="Microsoft Secure Score"
            value={formatPercent(secureScore.score_pct)}
            tone={secureScore.score_pct == null ? "neutral" : "accent"}
            size="lg"
            // A null delta is not "no change": it means fewer than two snapshots
            // were read, so nothing was compared.
            delta={
              secureScore.score_delta == null
                ? undefined
                : `${formatPointsDelta(secureScore.score_delta)} pts`
            }
            caption={
              secureScore.score_pct == null
                ? "The score read failed — unknown, not zero"
                : secureScore.score_delta == null
                  ? `${formatPoints(secureScore.current_score)} of ${formatPoints(
                      secureScore.max_score,
                    )} points · fewer than two snapshots, so no change could be measured`
                  : `${formatPoints(secureScore.current_score)} of ${formatPoints(
                      secureScore.max_score,
                    )} points, against the oldest snapshot in the window`
            }
          />
        </KpiTile>
      )}

      {identityLive && (
        <KpiTile href={`#${IDENTITY_ANCHOR}`} tone={HEALTH_TONE[identity.state]}>
          <Statistic
            label="Identity sensors unhealthy"
            value={formatCount(identity.sensors_unhealthy)}
            tone={
              identity.sensors_unhealthy == null
                ? "neutral"
                : cautionTone(identity.sensors_unhealthy)
            }
            size="lg"
            caption={
              identity.sensors_total == null
                ? "The sensor read failed — unknown, not zero unhealthy"
                : `${HEALTH_LABEL[identity.state]} · of ${formatCount(
                    identity.sensors_total,
                  )} sensors, with ${formatCount(identity.open_health_issues)} open health issues`
            }
          />
        </KpiTile>
      )}
    </div>
  );
}
